import { useState } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import profile from "../../../../assets/images/icons/profile.svg";
import warning from "../../../../assets/images/icons/warning.svg";
import { useQueryClient } from "@tanstack/react-query";
import { EndPoints } from "../../../../Constants/endpoints";
import { PatientDetails } from "../../../../interface/GeneralResponse";
import { RootState } from "../../../../redux/store";
import { useToast } from "../../../../Services/ToastContext";
import { truncateText } from "../../../../utils/stringsUtils";
import { AddPatientMode } from "./constants";
import { useAddPatientForm } from "./useAddPatientForm";
import { useAddPatientActions } from "./usePatientActions";
import { useAppSelector } from "../../../../hooks";
import {
  DateInput,
  GenderRadioSelect,
  GeneralButton,
  GeneralInput,
  Modal,
  PhoneAutocompleteInput,
  ProfileAvatar,
} from "../../../../components";
import { usePostData } from "../../../../hooks";

interface AddPatientProps {
  onClose: () => void;
  isAddModalOpen: boolean;
}

function AddPatient({ onClose, isAddModalOpen }: AddPatientProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const validate = useSelector((s: RootState) => s.phValidation);
  const registration = useAppSelector((s) => s.registration);
  const queryClient = useQueryClient();

  const [selectedPatient, setSelectedPatient] = useState<PatientDetails>();
  const [hidePatientInfoInputs, setHidePatientInfoInputs] =
    useState<boolean>(true);

  const mode = selectedPatient ? AddPatientMode.LINK : AddPatientMode.CREATE;
  const methods = useAddPatientForm({
    mode,
    t,
    validate,
  });
  const phoneValue = methods.watch("PhoneNumber");

  const actions = useAddPatientActions({
    selectedPatient,
    countryId: Number(registration.userDetails.CountryId),
    onSuccess: () => {
      methods.reset();
      handleCloseModal();
      queryClient.refetchQueries({
        queryKey: [EndPoints.GetMyPatient],
        exact: false,
      });
      queryClient.refetchQueries({
        queryKey: [EndPoints.SearchMyPatients],
        exact: false,
      });
      showToast(t("success"), "success");
    },
    onError: (error: any) => showToast(error || t("operation_failed"), "error"),
  });

  // Use separate mutation for updating patient
  const updatePatientMutation = usePostData(EndPoints.UpdatePatient);

 const onSubmit = (data: any) => {
   const formValues = {
     ...methods.getValues(),
     ...data,
   };

   if (mode === AddPatientMode.LINK && selectedPatient) {
     actions.link({
       Id: selectedPatient.Id,
       PhoneNumber: formValues.PhoneNumber || selectedPatient.PhoneNumber,
       FullName: formValues.FullName || selectedPatient.FullName,
       Birthday: formValues.Birthday || selectedPatient.Birthday,
       Gender: formValues.Gender || selectedPatient.Gender,
     });

     return;
   }

   actions.create({
     FullName: formValues.FullName,
     PhoneNumber: formValues.PhoneNumber,
     Birthday: formValues.Birthday,
     Gender: formValues.Gender,
   });
 };
  const isMasked = selectedPatient?.FullName?.includes("*");

  const onOptionSelectPatient = (option: PatientDetails) => {
    setSelectedPatient({ ...option });

    // Reset form with existing data, leaving null fields empty for user to fill
    methods.reset({
      ...option,
      Birthday:
        option.Birthday === null || option.Birthday === undefined
          ? ""
          : option.Birthday,
      Gender: option?.Gender
        ? option.Gender.charAt(0).toLocaleUpperCase() +
          option.Gender.slice(1).toLocaleLowerCase()
        : "",
    });
  };

  const handleClearSelection = () => {
    methods.reset({
      PhoneNumber: phoneValue,
      FullName: "",
    });
    setSelectedPatient(undefined);
  };

  const handleCloseModal = () => {
    onClose();
    setSelectedPatient(undefined);
    methods.reset({
      PhoneNumber: "",
      FullName: "",
      Birthday: "",
      Gender: "",
    });
  };

  // Check if we need to show overlay (only when ALL fields are filled)
  const shouldShowOverlay =
    hidePatientInfoInputs &&
    selectedPatient?.Birthday !== null &&
    selectedPatient?.Birthday !== undefined &&
    selectedPatient?.Birthday !== "" &&
    selectedPatient?.Gender !== null &&
    selectedPatient?.Gender !== undefined &&
    selectedPatient?.Gender !== "";

  // Check if we're in link mode with missing fields
  const isLinkModeWithMissingFields =
    mode === AddPatientMode.LINK &&
    (!selectedPatient?.Birthday || !selectedPatient?.Gender);

  // Check if update is in progress
  const isUpdating = updatePatientMutation.isPending;

  return (
    <Modal
      header={t("add_patient")}
      isOpen={isAddModalOpen}
      onClose={handleCloseModal}
    >
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          {isMasked && (
            <div className="flex items-center gap-4 rounded-lg border border-orange bg-warning-50 p-2 text-sm font-bold text-orange">
              <img src={warning} alt="" />
              <p>{t("this_user_is_already_have_an_account_on_dome_care")}</p>
            </div>
          )}

          <PhoneAutocompleteInput<{ Data: PatientDetails[] }, PatientDetails>
            name="PhoneNumber"
            controllerName="PhoneNumber"
            control={methods.control}
            getEndpoint={EndPoints.SearchMyPatients}
            onOptionSelect={(option: PatientDetails) =>
              onOptionSelectPatient(option)
            }
            onSearchResults={(options) => {
              if (!options || options?.length == 0) {
                handleClearSelection();
              }
              setHidePatientInfoInputs(options?.length > 0);
            }}
            getOptionValue={(o) => o.Id.toString()}
            getOptionLabel={(o) => ({
              value: o.PhoneNumber,
              label: (
                <div className="flex items-center gap-4">
                  {o.FullName.includes("*") ? (
                    <img src={profile} alt="" />
                  ) : (
                    <ProfileAvatar
                      Id={o.Id}
                      FullName={o.FullName}
                      src={o.Image}
                      size="small"
                      className="h-10 w-10 rounded-full"
                    />
                  )}
                  <span>{truncateText(o.FullName, 30)}</span>
                </div>
              ),
            })}
          />

          <div className="relative flex flex-col gap-4">
            {shouldShowOverlay && (
              <div className="absolute inset-0 z-10 bg-white/20" />
            )}

            <GeneralInput
              name="FullName"
              label={t("full_name")}
              placeholder={t("full_name")}
              charactersOnly
              maxLength={25}
              required
              disabled={mode === AddPatientMode.LINK}
            />

            <DateInput
              name="Birthday"
              label={t("birth_date")}
              dateConstraint="past"
              required={
                isLinkModeWithMissingFields || mode === AddPatientMode.CREATE
              }
              hideErrorMessage
              disabled={
                mode === AddPatientMode.LINK &&
                Boolean(selectedPatient?.Birthday)
              }
            />
            {methods.formState.errors.Birthday && (
              <p className="text-xs text-error">{t("required_field")}</p>
            )}

            <GenderRadioSelect
              name="Gender"
              aria-describedby="gender-description"
              required={
                isLinkModeWithMissingFields || mode === AddPatientMode.CREATE
              }
              disabled={
                mode === AddPatientMode.LINK && Boolean(selectedPatient?.Gender)
              }
            /> 
          </div>

          <div className="flex justify-end gap-2">
            <GeneralButton
              fontColor="body-text"
              title={t("cancel")}
              color="lite"
              onClick={() => {
                handleCloseModal();
              }}
            />
            <GeneralButton
              type="submit"
              color="blue"
              title={
                mode === AddPatientMode.LINK
                  ? t("link_to_patient") 
                  : t("add_patient")
              }
              isLoading={actions.isLoading || isUpdating}
            />
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
}

export default AddPatient;
